import {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table as MuiTable,
  Box,
  Typography,
} from "@mui/material";
import sumBy from "lodash-es/sumBy";
import { ReactElement } from "react";

import { InitialMessageSendFailureType } from "~frontend/__generated__/graphql";
import { noBorderBottomIf } from "~frontend/util/styling";

import { StatusTableHeaderCell } from "./styles";
import { MessageStatusBreakdown } from "./types";

export const initialSendFailureLabels: {
  [K in InitialMessageSendFailureType]: string;
} = {
  [InitialMessageSendFailureType.ContactOptedOut]: "Contact Opted Out",
  [InitialMessageSendFailureType.ContactOptedOutTemporarily]:
    "Contact On Strike List",
  [InitialMessageSendFailureType.ProviderError]: "Provider Error",
  [InitialMessageSendFailureType.ScriptRenderingFailed]:
    "Script Rendering Failed",
  [InitialMessageSendFailureType.MessageTooLong]: "Message Too Long",
  [InitialMessageSendFailureType.ContactSuppressed]: "Contact Suppressed",
  [InitialMessageSendFailureType.ContactNotSubscribed]:
    "Contact Not Subscribed",
  [InitialMessageSendFailureType.PersonalizedGifUnavailable]:
    "Personalized gif not generated",
  [InitialMessageSendFailureType.TcrCampaignError]: "10DLC Campaign Error",
  [InitialMessageSendFailureType.InternalError]: "Internal Error",
};

export default function SendStatusTable({
  messageStatusBreakdown,
}: {
  messageStatusBreakdown: MessageStatusBreakdown;
}): ReactElement {
  return (
    <Box sx={{ padding: "14px 16px 50px" }}>
      <Typography variant="body1">Send Status</Typography>
      <MuiTable>
        <TableHead>
          <TableRow>
            <StatusTableHeaderCell>Status</StatusTableHeaderCell>
            <StatusTableHeaderCell>Message Count</StatusTableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Not Sent Yet</TableCell>
            <TableCell>{messageStatusBreakdown.notSent}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>In Flight</TableCell>
            <TableCell>{messageStatusBreakdown.inFlight}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              sx={noBorderBottomIf(
                messageStatusBreakdown.initialSendFailures.length > 0
              )}
            >
              Initial Send Failure
            </TableCell>
            <TableCell
              sx={noBorderBottomIf(
                messageStatusBreakdown.initialSendFailures.length > 0
              )}
            >
              {sumBy(messageStatusBreakdown.initialSendFailures, "count")}
            </TableCell>
          </TableRow>
          {messageStatusBreakdown.initialSendFailures.map(
            ({ initialSendFailureType, count }, idx) => (
              <TableRow key={initialSendFailureType}>
                <TableCell
                  sx={{
                    paddingLeft: "32px",
                    ...noBorderBottomIf(
                      idx !==
                        messageStatusBreakdown.initialSendFailures.length - 1
                    ),
                  }}
                >
                  {initialSendFailureLabels[initialSendFailureType]}
                </TableCell>
                <TableCell
                  sx={noBorderBottomIf(
                    idx !==
                      messageStatusBreakdown.initialSendFailures.length - 1
                  )}
                >
                  {count}
                </TableCell>
              </TableRow>
            )
          )}
          <TableRow>
            <TableCell>Sent, No Delivery Report</TableCell>
            <TableCell>{messageStatusBreakdown.sentNoDeliveryReport}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Success</TableCell>
            <TableCell>{messageStatusBreakdown.sentDelivered}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Failure</TableCell>
            <TableCell>
              {sumBy(messageStatusBreakdown.sentFailed, "count")}
            </TableCell>
          </TableRow>
          {messageStatusBreakdown.sentUnknown > 0 && (
            <TableRow>
              <TableCell>Unknown</TableCell>
              <TableCell>{messageStatusBreakdown.sentUnknown}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </MuiTable>
    </Box>
  );
}
